import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const BlockHoursChart = ({ rotations }) => {
  // Group rotations by month and calculate total block hours
  const getMonthlyData = () => {
    const monthlyData = {};
    
    rotations.forEach(rotation => {
      const date = rotation.rotation_start_date || rotation.start_date;
      if (!date) return;
      
      const monthKey = date.substring(0, 7); // YYYY-MM
      const blockHours = rotation.total_block_hours || 0;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          blockHours: 0,
          count: 0
        };
      }
      
      monthlyData[monthKey].blockHours += blockHours;
      monthlyData[monthKey].count += 1;
    });
    
    // Convert to array and sort by month
    const dataArray = Object.values(monthlyData)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-12); // Last 12 months
    
    // Format month labels
    return dataArray.map(item => ({
      ...item,
      monthLabel: new Date(item.month + '-01').toLocaleDateString('en-US', { 
        month: 'short', 
        year: '2-digit' 
      })
    }));
  };

  const data = getMonthlyData();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-accent-cyan/20 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-1">{data.monthLabel}</p>
          <p className="text-sm text-primary">
            <strong>{data.blockHours.toFixed(1)}</strong> block hours
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {data.count} rotation{data.count !== 1 ? 's' : ''}
          </p>
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Block Hours Trend</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="TrendingUp" className="w-12 h-12 text-muted-foreground opacity-50 mb-3" />
          <p className="text-sm text-muted-foreground">
            No data available for block hours chart
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Block Hours Trend</h3>
          <p className="text-sm text-muted-foreground">Last 12 months of flight activity</p>
        </div>
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="TrendingUp" className="w-5 h-5 text-primary" />
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--accent-cyan) / 0.1)" />
          <XAxis 
            dataKey="monthLabel" 
            stroke="hsl(var(--muted-foreground))"
            style={{ fontSize: '0.75rem' }}
          />
          <YAxis 
            stroke="hsl(var(--muted-foreground))"
            style={{ fontSize: '0.75rem' }}
            label={{ 
              value: 'Block Hours', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ 
              paddingTop: '20px',
              fontSize: '0.875rem'
            }}
          />
          <Line 
            type="monotone" 
            dataKey="blockHours" 
            name="Block Hours"
            stroke="hsl(var(--primary))" 
            strokeWidth={2}
            dot={{ fill: 'hsl(var(--primary))', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BlockHoursChart;
